/**
 * The figures in the totals strip, worked out the way the SSRS report each chart replaces worked
 * them out, and nothing the data cannot honestly support.
 *
 * Pure, so every rule is in totals.spec rather than found on a client's chart. The rules, by report:
 *
 *   remainder     The Total is the mains meter's own reading, as the SSRS pie titled itself
 *                 ("Total: 256"), not the bars, which chart what is left of the mains after its
 *                 sub-meters and are floored at zero. With generation among the sub-meters, the
 *                 SSRS cards: imported, exported, net mains, generated, and the site's estimated use.
 *   consumption   Every meter shown added up, as SSRS did, but called what it is: mains and
 *                 sub-meters side by side count the same energy twice.
 *   policy        Every policy added up, unless they are in different units.
 *   any averaged  No total. Each group's own figure is its window average, weighted by readings, or
 *                 on a status chart its highest, lowest or average by its value type.
 */
import { ResourceSeriesVM } from '@models/backend-generated-models/models';
import { ReportKind } from './format';
import { isGenerationName, sumSeries } from './series-util';

/** The numbers the tiles show. Null where the data cannot support the figure honestly. */
export interface ResourceTotals {
  grossImport: number | null;
  grossExport: number | null;
  netMains: number | null;
  /** Null when no grid series could be identified. */
  generated: number | null;
  selfConsumed: number | null;
  /**
   * The chart's one total, or 0 where it has none. Kept as a number because the what-if reads it;
   * the tiles read `total`.
   */
  siteConsumption: number;
  /** The total the tiles show, or null where adding up means nothing (averages, mixed units). */
  total: number | null;
  /** What that total is: "Site consumption", "Total of meters shown", "Mains total"... */
  totalLabel: string;
  /** Said beside the tiles when a figure needs a caveat the number cannot carry. */
  note: string | null;
  /** One entry per group, whatever the groups turn out to be. */
  perSeries: { name: string; value: number; rgb: string }[];
}

export interface TotalsOptions {
  kind: ReportKind;
  averaged: boolean;
  /** The grid series of a grid and generation pair (see pickGrid), where the chart has one. */
  grid?: ResourceSeriesVM;
  /** The series are the response's own, so their server figures describe them. False for a what-if's. */
  metered: boolean;
}

/** A group's own figure over the window. */
export function seriesFigure(s: ResourceSeriesVM, averaged: boolean, metered: boolean): number {
  if (metered) {
    switch (s.valueTypeId) {
      case 2: if (s.windowMax != null) return s.windowMax; break;
      case 3: if (s.windowMin != null) return s.windowMin; break;
      case 4: if (s.windowAverage != null) return s.windowAverage; break;
    }
    if (averaged && s.windowAverage != null) return s.windowAverage;
  }
  const points = s.points ?? [];
  const sum = sumSeries(s, () => true);
  return averaged ? sum / Math.max(1, points.length) : sum;
}

const noSplit = { grossImport: null, grossExport: null, netMains: null, generated: null, selfConsumed: null };

export function computeTotals(series: ResourceSeriesVM[], opt: TotalsOptions): ResourceTotals {
  const perSeries = series.map(s => ({ name: s.name ?? '', value: seriesFigure(s, opt.averaged, opt.metered), rgb: s.rgb || '#999999' }));
  const units = new Set(series.map(s => (s.unit || '').trim().toLowerCase()).filter(Boolean));
  const mixedUnits = units.size > 1;

  const plain = (label: string, note: string | null = null): ResourceTotals => {
    const total = opt.averaged || mixedUnits ? null : perSeries.reduce((a, x) => a + x.value, 0);
    return { ...noSplit, siteConsumption: total ?? 0, total, totalLabel: label, note: note ?? (mixedUnits ? 'Not totalled: these are in different units.' : null), perSeries };
  };

  // A remainder chart whose total row carries the mains reading. One without a total row (a mains and
  // its solar, both plain groups) is a grid and generation pair like any other, below.
  const mains = opt.kind === 'remainder' && !opt.averaged ? series.find(s => s.isTotal) : undefined;
  const readings = mains?.points?.filter(p => p.sourceValue != null) ?? [];
  if (mains && readings.length) {

    const mainsTotal = readings.reduce((a, p) => a + (p.sourceValue ?? 0), 0);

    // Where the sub-meters read more than the mains, the remainder is floored at zero and the stack
    // is taller than the mains: say so rather than leave two totals that disagree.
    const subsAt = new Map<string, number>();
    for (const s of series) {
      if (s === mains) continue;
      for (const p of s.points ?? []) subsAt.set(String(p.date), (subsAt.get(String(p.date)) ?? 0) + (p.value ?? 0));
    }
    const over = readings.filter(p => (subsAt.get(String(p.date)) ?? 0) > (p.sourceValue ?? 0) + 0.5).length;
    const note = over ? `The sub-meters read more than the mains in ${over} of ${readings.length} bars, so the remainder shows 0 there.` : null;

    const generation = series.filter(s => s !== mains && isGenerationName(s.name));
    if (!generation.length) {
      return { ...noSplit, siteConsumption: mainsTotal, total: mainsTotal, totalLabel: 'Mains total', note, perSeries };
    }

    // The remainder's own cards: the mains imports and exports, generation adds to what the site used.
    const fromServer = opt.metered && mains.grossPositive != null && mains.grossNegative != null;
    const grossImport = fromServer ? mains.grossPositive! : readings.reduce((a, p) => a + Math.max(0, p.sourceValue ?? 0), 0);
    const grossExport = fromServer ? mains.grossNegative! : readings.reduce((a, p) => a + Math.max(0, -(p.sourceValue ?? 0)), 0);
    const generated = Math.abs(generation.reduce((a, s) => a + sumSeries(s, () => true), 0));
    const selfConsumed = generated - grossExport;
    const site = grossImport + selfConsumed;
    return {
      grossImport, grossExport, netMains: grossImport - grossExport, generated, selfConsumed,
      siteConsumption: site, total: site, totalLabel: 'Estimated site consumption', note, perSeries
    };
  }

  const grid = opt.grid;
  if (!grid || opt.averaged) return plain(opt.kind === 'consumption' ? 'Total of meters shown' : 'Total');

  const generated = series.filter(s => s !== grid).reduce((a, s) => a + sumSeries(s, () => true), 0);

  // The server's gross in and out where it has them. Summing the bars instead nets every hour of
  // export against the import in its bucket: an August that bought 90 MWh and sold 9 reads as 81
  // bought and nothing sold.
  const fromServer = opt.metered && !grid.isTotal && grid.grossPositive != null && grid.grossNegative != null;
  const grossImport = fromServer ? grid.grossPositive! : sumSeries(grid, v => v > 0);
  const grossExport = fromServer ? grid.grossNegative! : -sumSeries(grid, v => v < 0);
  const selfConsumed = generated - grossExport;
  // Import that is actually used on site, plus the generation that never left it.
  const site = grossImport + selfConsumed;

  return {
    grossImport, grossExport, netMains: grossImport - grossExport, generated, selfConsumed,
    siteConsumption: site, total: site, totalLabel: 'Site consumption', note: null, perSeries
  };
}
